import {
    CartPersistenceError,
    type CartItem,
    type CartPersistedState,
    type CartStorageEnvelope,
} from "../types/cart.types";

export const CART_STORAGE_KEY = "shopping_cart" as const;
export const CART_STORAGE_VERSION = 1 as const;

const EMPTY_UPDATED_AT = "1970-01-01T00:00:00.000Z";

export const createEmptyCartSnapshot = (): CartPersistedState => ({
    items: [],
    revision: 0,
    updatedAt: EMPTY_UPDATED_AT,
});

const isCartItemArray = (value: unknown): value is CartItem[] => Array.isArray(value);

export const parseCartSnapshot = (rawValue: string | null): CartPersistedState => {
    if (rawValue === null) return createEmptyCartSnapshot();

    try {
        const parsed = JSON.parse(rawValue) as {
            state?: { items?: unknown; revision?: unknown; updatedAt?: unknown };
            version?: unknown;
        };
        const items = parsed?.state?.items;

        if (!isCartItemArray(items)) {
            throw new CartPersistenceError(
                "STORAGE_CORRUPTED",
                "El estado persistido del carrito no contiene una lista de items válida.",
            );
        }

        const revision =
            typeof parsed.state?.revision === "number" &&
            Number.isFinite(parsed.state.revision) &&
            parsed.state.revision >= 0
                ? parsed.state.revision
                : 0;
        const updatedAt =
            typeof parsed.state?.updatedAt === "string" ? parsed.state.updatedAt : EMPTY_UPDATED_AT;

        return { items, revision, updatedAt };
    } catch (error) {
        if (error instanceof CartPersistenceError) throw error;

        throw new CartPersistenceError(
            "STORAGE_CORRUPTED",
            "No se pudo interpretar el estado persistido del carrito.",
            error,
        );
    }
};

const getLocalStorage = (): Storage => {
    if (typeof window === "undefined" || !window.localStorage) {
        throw new CartPersistenceError(
            "STORAGE_READ_FAILED",
            "localStorage no está disponible para leer el carrito.",
        );
    }

    return window.localStorage;
};

export const readCartSnapshot = (): CartPersistedState => {
    try {
        return parseCartSnapshot(getLocalStorage().getItem(CART_STORAGE_KEY));
    } catch (error) {
        if (error instanceof CartPersistenceError) throw error;

        throw new CartPersistenceError(
            "STORAGE_READ_FAILED",
            "No se pudo leer el carrito persistido.",
            error,
        );
    }
};

export const writeCartSnapshot = (snapshot: CartPersistedState): void => {
    const envelope: CartStorageEnvelope = {
        state: snapshot,
        version: CART_STORAGE_VERSION,
    };

    try {
        getLocalStorage().setItem(CART_STORAGE_KEY, JSON.stringify(envelope));
    } catch (error) {
        if (error instanceof CartPersistenceError) {
            throw new CartPersistenceError(
                "STORAGE_WRITE_FAILED",
                "localStorage no está disponible para guardar el carrito.",
                error,
            );
        }

        throw new CartPersistenceError(
            "STORAGE_WRITE_FAILED",
            "No se pudo guardar el carrito persistido.",
            error,
        );
    }
};
